import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * In-app section navigation hook with browser-history integration.
 *
 * Design principle: React state stays the source of truth. The browser's
 * history stack is a sidecar that narrates section changes so:
 *
 *   - Clicking a sidebar item pushes a history entry
 *   - Browser back pops to the previous section
 *   - When our history is exhausted, the browser exits the app naturally
 *     (we never create fake entries that would trap the user)
 *
 * Each pushed entry carries `{ pwdSection: <id> }` on the history state
 * object. The initial entry is claimed via `replaceState` at mount — no
 * synthetic back target created.
 *
 * Generic over the tab-id type so the same hook can drive other V2 apps
 * (e.g. finance-vite) with a different set of section ids.
 */
export function useSectionNav<T extends string>(initialSection: T): [T, (next: T) => void] {
  const [section, setSectionState] = useState<T>(initialSection);
  const sectionRef = useRef<T>(initialSection);

  useEffect(() => {
    sectionRef.current = section;
  }, [section]);

  useEffect(() => {
    // Claim the initial history slot without creating a fake back target.
    const currentState = (window.history.state as Record<string, unknown> | null) || {};
    if (currentState.pwdSection !== initialSection) {
      window.history.replaceState({ ...currentState, pwdSection: initialSection }, '', '');
    }

    function handlePop(event: PopStateEvent) {
      const state = (event.state as Record<string, unknown> | null) || {};
      const next = state.pwdSection;
      if (typeof next === 'string' && next !== sectionRef.current) {
        setSectionState(next as T);
      }
      // If pwdSection is absent, this was either a drawer pop (handled in
      // Drawer's own listener) or the user walking past our initial entry.
      // Either way, do nothing here and let the other listener / browser act.
    }

    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSection = useCallback((next: T) => {
    if (next === sectionRef.current) return;
    const currentState = (window.history.state as Record<string, unknown> | null) || {};
    window.history.pushState({ ...currentState, pwdSection: next }, '', '');
    setSectionState(next);
  }, []);

  return [section, setSection];
}
